import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { t } from "ttag";
import {
  getReleaseNotesMarkdown,
  hasReleaseNotes,
} from "./firmwareReleaseNotes";

interface FirmwareReleaseNotesHelpProps {
  releaseNotes?: any;
  versionLabel?: string;
  size?: "small" | "medium";
  className?: string;
}

const markdownSx = {
  fontSize: "0.9rem",
  lineHeight: 1.5,
  "& p": { margin: "0 0 0.75rem" },
  "& h1, & h2, & h3, & h4": {
    margin: "0.75rem 0 0.5rem",
    fontSize: "1rem",
    fontWeight: 600,
  },
  "& ul, & ol": { margin: "0 0 0.75rem", paddingLeft: "1.25rem" },
  "& code": {
    fontFamily: "monospace",
    fontSize: "0.85em",
    backgroundColor: "rgba(0,0,0,0.06)",
    padding: "0.1em 0.3em",
    borderRadius: 3,
  },
  "& pre": {
    backgroundColor: "rgba(0,0,0,0.06)",
    padding: "0.75rem",
    borderRadius: 4,
    overflowX: "auto",
  },
  "& a": { color: "inherit" },
};

const FirmwareReleaseNotesHelp = ({
  releaseNotes,
  versionLabel,
  size = "small",
  className,
}: FirmwareReleaseNotesHelpProps) => {
  const [open, setOpen] = useState(false);
  const markdown = getReleaseNotesMarkdown(releaseNotes);
  const hasNotes = hasReleaseNotes(releaseNotes);

  return (
    <>
      <IconButton
        className={className}
        size={size}
        onClick={() => setOpen(true)}
        title={hasNotes ? t`Release notes` : t`No release notes`}
        aria-label={t`Release notes`}
      >
        <InfoOutlined fontSize="inherit" />
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {versionLabel
            ? `${t`Release notes`} — ${versionLabel}`
            : t`Release notes`}
        </DialogTitle>
        <DialogContent dividers>
          {hasNotes ? (
            <Typography component="div" sx={markdownSx}>
              <ReactMarkdown>{markdown}</ReactMarkdown>
            </Typography>
          ) : (
            <Typography color="textSecondary">
              {t`No release notes for this version.`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t`Close`}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FirmwareReleaseNotesHelp;
